import posthog from 'posthog-js'
import { ref } from 'vue'

let isReady = false
const queue = []

/** Multivariate experiment flag driving the Inventory redesign rollout. */
export const INVENTORY_BETA_FLAG = 'inventory-beta'
export const INVENTORY_BETA_TEST = 'test'
export const INVENTORY_BETA_CONTROL = 'control'

/**
 * The `inventory-beta` variant for the current user: `'test'`, `'control'`, or
 * `undefined` while flags are still unresolved (see `isInventoryBetaResolved`).
 *
 * A `ref`, not a plain read, because the gate is consumed by a Pinia getter and
 * dozens of `computed`s — PostHog resolves flags asynchronously and again after
 * `identify()`, so consumers have to re-evaluate when the answer arrives.
 */
export const inventoryBetaVariant = ref(undefined)

/** `false` until PostHog has produced a value (cached or fresh) for the flag. */
export const isInventoryBetaResolved = ref(false)

/**
 * The distinct id PostHog held when `inventoryBetaVariant` was last read.
 *
 * "Resolved" is not the same as "resolved for this dealer". The `/flags`
 * request that posthog-js fires at init goes out under whatever id the browser
 * already holds — an anonymous one on a fresh browser or right after sign-in,
 * the previous account's on a shared one — and its answer (`false` for an
 * anonymous visitor: the release condition is a person property they don't
 * have) stands until the reload that `identify()` triggers comes back. Read as
 * the dealer's own answer, that put a beta dealer on legacy for that window.
 * `UserStore.isInventoryPilot` compares this against the signed-in user and
 * treats a mismatch as unresolved.
 */
export const inventoryBetaDistinctId = ref(null)

/**
 * Product Tags — a plain boolean PostHog flag, gating every tag surface (see
 * `isTagsEnabled` in helpers/featureFlags.js, the only reader).
 *
 * A `ref` for the same reason as the beta variant above: flags resolve
 * asynchronously and again after `identify()`, and the grids' column sets,
 * the panels and the filter menu are all `computed`s that have to re-run when
 * the answer lands. Unresolved reads as OFF: tags is a new feature with no
 * persisted opt-in to fall back on, so nobody can lose something they had.
 */
export const TAGS_FLAG = 'tags'
export const tagsFlagEnabled = ref(false)

/**
 * `true` when PostHog has put this user in the beta's test variant — the
 * PostHog half of the gate, OR-ed with the email allowlist in
 * `UserStore.isInventoryPilot` (the only consumer, and the single predicate
 * every part of the beta reads).
 *
 * `true` counts alongside `'test'`: that's what a BOOLEAN flag returns, so
 * configuring `inventory-beta` as boolean rather than multivariate degrades to
 * "everyone targeted is in" instead of locking the whole cohort out.
 */
export const isInventoryBetaTest = () =>
  inventoryBetaVariant.value === INVENTORY_BETA_TEST || inventoryBetaVariant.value === true

/**
 * Person-property values this browser has already sent, per PostHog person.
 *
 * The obvious implementation writes the variant on every read, because `$set`
 * is idempotent. Server-side it is — but a `$set` is a CAPTURE, a capture makes
 * PostHog re-evaluate flags, and the re-evaluation fires `onFeatureFlags`,
 * which is where the variant write lives. Writing unconditionally there is an
 * unbounded /flags loop. Writing on CHANGE keeps the property current without
 * the request storm.
 *
 * "Changed since when" has to outlive the page. A module-level guard resets on
 * every full load, and the `user` watcher in App.vue re-runs every mirror each
 * time the user object is replaced — which the route guard's `fetchUser` does
 * on navigation — so an unchanged value went out as a billable `$set` (plus a
 * `/flags` reload) on most page views. Hence localStorage, with memory as the
 * fallback when storage is blocked.
 *
 * Keyed by distinct id because nothing calls `posthog.reset()` on sign-out:
 * on a shared browser one dealer's write must not suppress another's.
 *
 * A record expires after a week, deliberately. A write recorded here can still
 * have been lost in flight (tab closed before PostHog's queue flushed), and the
 * re-assert on load exists precisely to repair that — so an unchanged value is
 * re-sent at most weekly, rather than never (no repair) or on every page view.
 */
const SENT_PERSON_PROPS_PREFIX = 'cdp_posthog_person_props:'
const SENT_PERSON_PROPS_TTL_MS = 7 * 24 * 60 * 60 * 1000
const sentPersonPropsMemory = {}

const readSentPersonProps = (distinctId) => {
  let stored = {}

  try {
    stored = JSON.parse(localStorage.getItem(SENT_PERSON_PROPS_PREFIX + distinctId)) || {}
  } catch {
    // Blocked or corrupt storage: the in-memory copy below still guards this page.
  }

  return { ...stored, ...(sentPersonPropsMemory[distinctId] || {}) }
}

const wasPersonPropSent = (prop, value, distinctId = posthog.get_distinct_id?.()) => {
  if (!distinctId) return false

  const entry = readSentPersonProps(distinctId)[prop]

  return !!entry && entry.value === value && Date.now() - entry.at < SENT_PERSON_PROPS_TTL_MS
}

// Recorded BEFORE the write goes out, so the flag reload the write triggers
// can't re-enter a mirror and send it a second time.
const recordPersonPropsSent = (props, distinctId = posthog.get_distinct_id?.()) => {
  if (!distinctId) return

  const at = Date.now()
  const next = readSentPersonProps(distinctId)

  Object.entries(props).forEach(([prop, value]) => {
    next[prop] = { value, at }
  })

  sentPersonPropsMemory[distinctId] = next

  try {
    localStorage.setItem(SENT_PERSON_PROPS_PREFIX + distinctId, JSON.stringify(next))
  } catch {
    // Storage blocked: memory still guards this page, which is the old behaviour.
  }
}

/**
 * Whether this browser is attached to a signed-in dealer. The flag sync below
 * runs for every visitor, the sign-in page included, and posthog-js turns a
 * `$set` from an anonymous visitor into a new person profile, so the mirrors
 * written from it must skip anyone who hasn't been identified yet. They run
 * again after `identify()`, which is when a new dealer gets their values.
 */
const isIdentifiedPerson = () => posthog.get_property?.('$user_state') === 'identified'

/**
 * The dealer's ARM as a reportable value: `'test'`, `'control'`, or `null` when
 * they aren't enrolled or flags haven't resolved.
 *
 * `true` maps to `test` because a BOOLEAN-configured flag returns it, and the
 * rest of the app already treats that as "in". `null` rather than `false` or
 * `undefined`: "not enrolled" has no arm, and a falsy string would put those
 * dealers into arm-filtered reports.
 */
const normaliseArm = (variant) => {
  const value = variant === true ? INVENTORY_BETA_TEST : variant

  return value === INVENTORY_BETA_TEST || value === INVENTORY_BETA_CONTROL ? value : null
}

/**
 * The arm to stamp on an event: the same value the person property
 * carries, from the same normalisation, so an event and its person can never
 * disagree about which arm the dealer is in.
 */
export const inventoryBetaArm = () => normaliseArm(inventoryBetaVariant.value)

/**
 * Mirror the assigned arm onto the person.
 *
 * This is what makes the experiment's actual hypothesis measurable: PostHog
 * retention insights cannot break down by a flag variant, only by a person
 * property, so without this the test-vs-control retention comparison is not
 * buildable at all.
 *
 * Never written for `false`/`undefined`: a dealer who was never enrolled
 * has NO variant, which is a different thing from a variant of "false" —
 * writing one would drop non-enrolled dealers into the retention tiles. A
 * boolean-configured flag (`true`) maps to `test`, matching how the rest of the
 * app reads it.
 */
export const mirrorInventoryBetaVariant = (variant) => {
  const value = normaliseArm(variant)

  if (!value || !isIdentifiedPerson()) return
  if (wasPersonPropSent('inventory_beta_variant', value)) return

  recordPersonPropsSent({ inventory_beta_variant: value })
  posthog.setPersonProperties?.({ inventory_beta_variant: value })
}

/**
 * Mirror which experience the dealer is in — `opted_in` or
 * `opted_out`. The opt-out survey targets on it, and a survey cannot target an
 * event property.
 *
 * Call it BEFORE the opt event: person-on-events stamps the value at ingestion,
 * so writing after would leave the event that marks the change carrying the
 * value from before it.
 */
export const mirrorInventoryBetaStatus = (status) => {
  const apply = () => {
    if (!status || wasPersonPropSent('inventory_beta_status', status)) return

    recordPersonPropsSent({ inventory_beta_status: status })
    posthog.setPersonProperties?.({ inventory_beta_status: status })
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * Mirror the dealer's plan onto the person (`plan`: "Prospect", "Majors", "All-Star", …), so any insight —
 * retention above all, which can only break down by a person property — can split by plan.
 *
 * Change-guarded like the mirrors above: called on every load, it only goes out when the plan differs from
 * what this browser last sent for this person — a new browser (a sign-in) or a plan change — plus the weekly
 * repair re-send. Never from an anonymous visitor.
 */
export const mirrorPlan = (plan) => {
  const apply = () => {
    if (!plan || !isIdentifiedPerson() || wasPersonPropSent('plan', plan)) return

    recordPersonPropsSent({ plan })
    posthog.setPersonProperties?.({ plan })
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * Mirror "this dealer has earned the beta survey" onto the person.
 *
 * The eligibility itself is an EVENT (`inventory_beta.survey_eligible`) because
 * it is a moment: the four conditions in
 * `UserStore.markInventoryBetaSurveyEligible` became true at a point in time.
 * But a PostHog survey cannot be targeted on an event or on an event property —
 * only on person properties and cohorts — so the moment has to leave a mark on
 * the person for the survey to find. Same reason `inventory_beta_status` exists.
 *
 * Written BEFORE the event, like the status mirror: person-on-events stamps at
 * ingestion, so writing after would leave the very event that marks the
 * eligibility carrying the value from before it.
 *
 * `$set` the boolean (what the survey targets) and `$set_once` the timestamp
 * (when they earned it — usable as a cohort start date, so a re-assert must not
 * move it).
 *
 * Change-guarded like the others: a `$set` is a capture, and a capture makes
 * PostHog re-evaluate flags. The once-ever guard lives on the USER RECORD
 * (`inventory_beta_survey_fired_at`), not here — this runs again on every load
 * via `mirrorInventoryBetaState`, deliberately, so an eligibility whose PostHog
 * write was lost still reaches the survey. Without that re-assert the record's
 * write-once guard would mean it never retries and that dealer is never asked.
 */
export const mirrorInventoryBetaSurveyEligible = (at) => {
  const apply = () => {
    if (wasPersonPropSent('inventory_beta_survey_eligible', true)) return

    recordPersonPropsSent({ inventory_beta_survey_eligible: true })
    posthog.setPersonProperties?.(
      { inventory_beta_survey_eligible: true },
      { inventory_beta_survey_eligible_at: at },
    )
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * Read the flag through `getFeatureFlag` rather than the `onFeatureFlags`
 * callback payload, because that call is what emits `$feature_flag_called` —
 * the exposure event the experiment needs to attribute a user to a variant.
 * PostHog de-dupes it per flag+value per session, so calling it on every
 * refresh is cheap.
 */
const syncInventoryBeta = () => {
  inventoryBetaDistinctId.value = posthog.get_distinct_id?.() ?? null
  inventoryBetaVariant.value = posthog.getFeatureFlag(INVENTORY_BETA_FLAG)
  isInventoryBetaResolved.value = true

  // Stamp when this person first entered the experiment.
  //
  // `set_once`, so the value is the FIRST time we saw them in `test` and never
  // moves — every later sync (and this runs on `loaded`, on each `/flags`
  // response and after every `identify`) leaves it alone. That makes it usable
  // as a cohort start date: time-to-opt-out and return-rate are measured from
  // it, and a property that drifted forward on every page load would make both
  // meaningless.
  //
  // Deliberately NOT written for `control` — the property marks entry into the
  // treatment, and setting it for everyone exposed would make "in the
  // experiment" unqueryable.
  // Guarded by the sent-props record, not per page life. `set_once` is still a
  // `$set` capture, and a capture makes PostHog re-evaluate flags, which fires
  // `onFeatureFlags`, which calls this function again — an unbounded /flags
  // loop. The server-side `set_once` semantics stop the VALUE changing; they do
  // nothing about the request, so a per-page guard still billed one per load.
  if (
    inventoryBetaVariant.value === INVENTORY_BETA_TEST
    && isIdentifiedPerson()
    && !wasPersonPropSent('experiment_started_at', true)
  ) {
    recordPersonPropsSent({ experiment_started_at: true })
    posthog.people?.set_once?.({ experiment_started_at: new Date().toISOString() })
  }

  // The assigned arm, as a person property — see `mirrorInventoryBetaVariant`.
  // Here rather than at a call site because this is the ONE place the flag is
  // read, so the mirror cannot drift from what the app itself believes.
  mirrorInventoryBetaVariant(inventoryBetaVariant.value)
}

/**
 * Read-only on purpose: no `$set`, no capture. The beta sync above has to guard
 * its person-property writes against an unbounded /flags loop (a capture
 * re-evaluates flags, which calls the sync again); this one writes nothing, so
 * re-running it on every evaluation costs nothing.
 */
const syncTagsFlag = () => {
  tagsFlagEnabled.value = !!posthog.isFeatureEnabled(TAGS_FLAG)
}

export const markPosthogReady = () => {
  isReady = true
  queue.forEach(fn => fn())
  queue.length = 0

  // By the time `loaded` fires, PostHog has rehydrated the flags it persisted
  // last session — so a returning beta user is resolved before first paint and
  // never flickers through the classic app.
  syncInventoryBeta()
  syncTagsFlag()

  // Fires again on the fresh `/flags` response and after every `identify()`,
  // which is when an anonymous visitor first becomes a known experiment
  // participant.
  posthog.onFeatureFlags(syncInventoryBeta)
  posthog.onFeatureFlags(syncTagsFlag)

  // Drop the flag overrides the old opt-out path wrote — see the function.
  clearLegacyInventoryBetaOverride()
}

/**
 * Mirror an enrollment onto the PostHog person, so the flag can target it.
 *
 * The app cannot hand out a flag — assignment is PostHog's. What it
 * can do is state the fact the release condition targets: this dealer created
 * their first batch, so the beta now has something to show them. Point
 * `inventory-beta` at `inventory_beta_eligible = true` and PostHog randomises
 * them into an arm.
 *
 * A MIRROR, not the record: the durable "already enrolled" answer lives on the
 * user record (`UserStore.inventoryBetaEligibleAt`). This is called both at
 * enrollment and on every later load that has one, because `$set` is idempotent
 * and an enrollment whose PostHog write was lost — closed tab, dropped request
 * — would otherwise never enter the experiment at all.
 *
 * `set_once` for the timestamp: it marks when they entered and is only
 * meaningful as a cohort start date, so a re-assert must not move it.
 *
 * `reloadFeatureFlags` so the new arm lands without a hard refresh. It does NOT
 * read the flag back — person properties are eventually consistent and a
 * same-tick read returns the stale value; the read happens in
 * `onFeatureFlags`, wired in `markPosthogReady`.
 *
 * Change-guarded like the other mirrors. It was the one that wasn't: every
 * `user` watcher fire sent a `$set` AND a flag reload, and the route guard
 * replaces the user on navigation — so an enrolled dealer paid both on nearly
 * every page. The reload now only follows a write that actually goes out,
 * i.e. when the targeting property is new (or due its weekly re-assert).
 */
export const mirrorInventoryBetaEligibility = (at, via) => {
  const apply = () => {
    if (wasPersonPropSent('inventory_beta_eligible', true)) return

    recordPersonPropsSent({ inventory_beta_eligible: true })
    posthog.setPersonProperties?.(
      { inventory_beta_eligible: true, ...(via ? { inventory_beta_eligible_via: via } : {}) },
      { inventory_beta_eligible_at: at },
    )

    posthog.reloadFeatureFlags?.()
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * PostHog's own session id — the session it will attribute a survey to, which
 * is why the "is this a later session?" comparison uses it rather than our
 * `helpers/session.js` stamp.
 *
 * Confirmed present in posthog-js 1.393.4 (`get_session_id(): string`). Returns
 * `null` if it's ever missing, and callers treat `null` as "can't tell" — which
 * means don't fire, because firing the survey in the same session the dealer
 * opted in is the failure this rule exists to prevent.
 */
export const posthogSessionId = () => {
  try {
    return posthog.get_session_id?.() || null
  } catch {
    return null
  }
}

export const safeCapture = (event, properties = {}) => {
  if (isReady) {
    posthog.capture(event, properties)
  } else {
    queue.push(() => posthog.capture(event, properties))
  }
}

/**
 * Switch the browser to `distinctId`, change-guarding the properties.
 *
 * posthog-js only folds identify properties into the `$identify` event when
 * the browser was ANONYMOUS. When it is already identified as someone else —
 * which, with no `posthog.reset()` on sign-out, is every second account on a
 * shared browser and every impersonation tab — it switches the id and then
 * sends the properties as a standalone `$set`. Two accounts alternating in one
 * browser paid that `$set` on every switch, for values the person already has.
 *
 * So in that case the properties go out only if this browser hasn't sent these
 * exact values for this person recently, using the same record as the mirrors.
 * From an anonymous browser they always ride along: they cost no extra event
 * there, and a new dealer's first identify is where their profile gets them.
 */
const identifyPerson = (distinctId, properties) => {
  const id = String(distinctId)
  const entries = Object.entries(properties || {})
  const alreadySent = isIdentifiedPerson()
    && entries.every(([prop, value]) => wasPersonPropSent(prop, value, id))

  if (alreadySent) {
    posthog.identify(id)
    return
  }

  if (entries.length) recordPersonPropsSent(properties, id)
  posthog.identify(id, properties)
}

/**
 * Skipped when the browser is already this dealer. Nothing calls
 * `posthog.reset()` on sign-out, so a returning dealer signs in already
 * identified, and posthog-js answers a repeat `identify()` that carries
 * properties with a `$set` plus a flag reload: one of each per sign-in, for
 * values the person already has.
 */
export const safeIdentify = (distinctId, properties = {}) => {
  const apply = () => {
    if (posthog.get_distinct_id() === String(distinctId)) return

    identifyPerson(distinctId, properties)
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * Make sure PostHog knows who this is, whether or not they signed in during
 * this session. `safeIdentify` only runs on the sign-in and account-creation
 * paths, so a returning user restoring a persisted session was evaluated
 * against whatever distinct id the browser happened to hold — fine for
 * autocapture, not fine for an experiment that targets specific people.
 *
 * A no-op once the ids already match, so it can be called from a `user` watcher
 * without re-identifying on every profile change.
 */
export const ensureIdentified = (user) => {
  if (!user?.id) return

  const distinctId = String(user.id)

  const apply = () => {
    const isSamePerson = posthog.get_distinct_id() === distinctId

    // The `tags` flag targets by email (and `id` rides along for any flag that
    // targets by person id), and a person property set by `identify()` only
    // reaches flag evaluation after a server round trip — so both are handed
    // to the evaluator directly
    // (PostHog's own advice for these flags). This sends no `$set`; it only
    // stores them locally for `/flags` requests.
    //
    // Compared against what PostHog already persisted for flag evaluation,
    // not a module variable: that variable reset on every page load, and each
    // call reloads flags. When `identify()` below is about to switch the
    // person it reloads flags itself, so the props ride on that request
    // instead of costing a second one.
    const flagProps = { id: distinctId, ...(user.email ? { email: user.email } : {}) }
    const stored = posthog.get_property?.('$stored_person_properties') || {}
    const flagPropsStale = Object.entries(flagProps).some(([key, value]) => stored[key] !== value)

    if (flagPropsStale) posthog.setPersonPropertiesForFlags?.(flagProps, isSamePerson)

    if (isSamePerson) return

    identifyPerson(distinctId, {
      email: user.email,
      name: user.name,
      business_name: user.business_name || '',
    })
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * ONE-TIME cleanup of the flag overrides this app used to write.
 *
 * Until now, switching to legacy pinned the browser to `control` and switching
 * back cleared the pin. That is correct for a plain beta toggle and WRONG for
 * an A/B experiment running on the same flag: the flag value IS the dealer's
 * assignment, so writing it silently moved them out of the arm they were
 * randomised into and took their outcome data with them. (It also landed in
 * PostHog before the opt-out event was captured, which is why most
 * `inventory_beta.opted_out` events carry `$feature/inventory-beta = false`.)
 *
 * The writes are gone, but PostHog persisted them — every dealer who ever
 * opted out still carries `$override_feature_flags` in this browser's storage
 * and would drag a stale `control` into the new run. So clear it exactly once
 * per browser and record that we did.
 *
 * Once, not on every boot, because `overrideFeatureFlags(false)` clears ALL
 * client overrides — including one a developer set from the PostHog toolbar,
 * which should survive a page load.
 */
const OVERRIDE_CLEARED_KEY = 'cdp_inventory_beta_override_cleared'

export const clearLegacyInventoryBetaOverride = () => {
  const apply = () => {
    try {
      if (localStorage.getItem(OVERRIDE_CLEARED_KEY)) return

      posthog.featureFlags?.overrideFeatureFlags?.(false)
      localStorage.setItem(OVERRIDE_CLEARED_KEY, new Date().toISOString())
      syncInventoryBeta()
    } catch {
      // Storage blocked (private window). Skipping the cleanup is safe: the
      // override it removes lives in that same blocked storage.
    }
  }

  isReady ? apply() : queue.push(apply)
}
